import {
  Add,
  BuildOutlined,
  Delete,
  DescriptionOutlined,
  FileDownloadOutlined,
  Payment,
  SavingsOutlined,
  TrendingDown,
  TrendingUp,
  VisibilityOutlined,
  WorkOutline,
} from "@mui/icons-material";
import { Box, CircularProgress } from "@mui/material";
import { useEffect, useState } from "react";
import { useNavigate, useParams, useSearchParams } from "react-router-dom";
import { toast } from "react-toastify";

import { ExpenseList } from "../../components/expense";
import { get_date } from "../../components/generalComponets";
import { AddPerformanceGuarantees } from "../../components/performanceGuarantee";
import {
  AddDocumentModal,
  AddEditProjectStage,
  BudgetOverview,
  CustomerInfo,
  MoveTool,
  ProjectActions,
  ProjectContracts,
  ProjectDetails,
  ProjectHeader,
  ProjectIncome,
  ProjectPurchase,
  ProjectTools,
  ProjectWork,
  StageOverview,
  TaskOverview,
  TeamInfo,
  TenderDetails,
} from "../../components/project";
import { PROJECT_ROUTES } from "./constants";
import {
  ADD_CLIENT_INCOME,
  ADD_WORK,
  CREATE_EXPENSE,
  CREATE_GOVERNMENT_INVOICE,
  CREATE_PERFORMANCE_GUARANTEE,
  CREATE_PURCHASE,
  CREATE_SUBCONTRACTOR_TRANSACTION,
  CREATE_TOOL,
  DELETE_PROJECT,
  EDIT_PROJECT,
  GENERATE_PROJECT_REPORT,
  VIEW_ALL_TASK,
  VIEW_ASSIGNED_TASK,
  VIEW_CLIENT,
  VIEW_EXPENSE,
  VIEW_GOVERNMENT_INVOICE,
  VIEW_PERFORMANCE_GUARANTEE,
  VIEW_PROJECT,
  VIEW_STAGE,
  VIEW_TOOL,
} from "../../constants";
import { PageLayout } from "../../layouts/PageLayout";
import { CollapsibleCard, DeleteModal, PageHeaderTabs } from "../../components/v3";
import {
  AnalyticsService,
  CRMService,
  FileService,
  PerformanceGuaranteesService,
  ProjectService,
  ToolService,
} from "../../services";
import { formatRupees, useHasPermission } from "../../utils";
import { useTabNavigate } from "../../hooks/usePersistedTableState";

export const SingleProject = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  // Tab switches replace the history entry, so Back leaves the page in one step; each tab's table keeps its filters.
  const navigateTab = useTabNavigate();
  const [searchParams] = useSearchParams();
  const currentTab = searchParams.get("tab");

  const [projectDetails, setProjectDetails] = useState({});
  const [projectSummary, setProjectSummary] = useState({});
  const [performanceGuarantees, setPerformanceGuarantees] = useState([]);
  const [invoices, setInvoices] = useState([]);
  const [projectTools, setProjectTools] = useState([]);
  const [toolCount, setToolCount] = useState(0);
  const [tools, setTools] = useState([]);
  const [activeTab, setActiveTab] = useState(currentTab || "overview");
  const [reloadDetails, setReloadDetails] = useState(0);
  const [reloadTools, setReloadTools] = useState(0);
  const [openStageModal, setOpenStageModal] = useState(false);
  const [openPgModal, setOpenPgModal] = useState(false);
  const [openToolModal, setOpenToolModal] = useState(false);
  const [openDocModal, setOpenDocModal] = useState(false);
  const [documents, setDocuments] = useState([]);
  const [docCount, setDocCount] = useState(0);

  // Section-specific loading states to prevent page-wide flickering
  const [loading, setLoading] = useState(false); // General project loading
  const [pgLoading, setPgLoading] = useState(false);
  const [invoicesLoading, setInvoicesLoading] = useState(false);
  const [toolsLoading, setToolsLoading] = useState(false);
  const [docsLoading, setDocsLoading] = useState(false);
  const [viewingDocId, setViewingDocId] = useState(null);

  const [deleteModal, setDeleteModal] = useState({
    isOpen: false,
    projectId: null,
  });
  const [docDeleteModal, setDocDeleteModal] = useState({
    isOpen: false,
    docId: null,
  });

  const hasViewOverview = useHasPermission(VIEW_PROJECT);
  const hasAddIncome = useHasPermission(ADD_CLIENT_INCOME);
  const hasViewExpense = useHasPermission(VIEW_EXPENSE);
  const hasAddExpense = useHasPermission(CREATE_EXPENSE);
  const hasAddWork = useHasPermission(ADD_WORK);
  const hasAddPurchase = useHasPermission(CREATE_PURCHASE);
  const hasCreateTransaction = useHasPermission(CREATE_SUBCONTRACTOR_TRANSACTION);
  const hasToolCreate = useHasPermission(CREATE_TOOL);
  const hasViewTool = useHasPermission(VIEW_TOOL);
  const hasViewAllTask = useHasPermission(VIEW_ALL_TASK);
  const hasViewAssignedTask = useHasPermission(VIEW_ASSIGNED_TASK);
  const hasViewStage = useHasPermission(VIEW_STAGE);
  const hasCreatePerformanceGuarantee = useHasPermission(CREATE_PERFORMANCE_GUARANTEE);
  const hasDownloadReport = useHasPermission(GENERATE_PROJECT_REPORT);
  const hasDeleteProject = useHasPermission(DELETE_PROJECT);
  const hasTeamInfo = false;
  const hasClientInfo = useHasPermission(VIEW_CLIENT);
  const hasViewPg = useHasPermission(VIEW_PERFORMANCE_GUARANTEE);
  const hasCreateInvoice = useHasPermission(CREATE_GOVERNMENT_INVOICE);
  const hasViewInvoice = useHasPermission(VIEW_GOVERNMENT_INVOICE);
  const hasEditProject = useHasPermission(EDIT_PROJECT);

  useEffect(() => {
    if (activeTab === "overview") {
      fetchData(id);
    }
  }, [activeTab, id, reloadDetails]);

  const fetchData = async (projectId) => {
    setLoading(true);
    try {
      const { data: projectData } = await ProjectService.getProjectById(projectId);
      const { data: projectAnalytics } = await AnalyticsService.getCompanyProjectSummary({ ids: projectId, page: 1, limit: 1 });
      setProjectDetails(projectData);
      setProjectSummary(projectAnalytics?.rows[0]);

      // Parallel fetch for overview components
      fetchPgData(projectId);
      fetchInvoices(projectId);
      fetchTools(projectData?.storeId);
      fetchDocuments(projectId);
    } catch (error) {
      console.error("🚀 ~ fetchData ~ error:", error);
      toast.error("Failed to fetch project details");
    } finally {
      setLoading(false);
    }
  };

  const fetchPgData = async (projectId) => {
    try {
      setPgLoading(true);
      const { data: pgData } = await PerformanceGuaranteesService.getPerformanceGuarantees({
        projectId: projectId,
        sortBy: "updatedAt",
        sortOrder: "desc",
        page: 1,
        limit: 2,
      });
      setPerformanceGuarantees(pgData);
    } catch (error) {
      console.error("🚀 ~ fetchPgData ~ error:", error);
      toast.error("Failed to fetch performance guarantees");
    } finally {
      setPgLoading(false);
    }
  };

  const fetchInvoices = async (projectId) => {
    try {
      setInvoicesLoading(true);
      const { data: invoicesData } = await CRMService.getClientInvoices({
        projectId: projectId,
        sortBy: "updatedAt",
        sortOrder: "desc",
        page: 1,
        limit: 2,
      });
      setInvoices(invoicesData?.rows);
    } catch (error) {
      console.error("🚀 ~ fetchInvoices ~ error:", error);
      toast.error("Failed to fetch invoices");
    } finally {
      setInvoicesLoading(false);
    }
  };

  const fetchTools = async (storeId) => {
    try {
      setToolsLoading(true);
      const { data: toolInventory } = await ToolService.getToolInventory({
        storeIds: [storeId],
        sortBy: "updatedAt",
        sortOrder: "desc",
        page: 1,
        limit: 2,
      });
      const toolIds = toolInventory?.rows.map((tool) => tool.toolId);
      if (toolIds?.length > 0) {
        const { data: toolsResponse } = await ToolService.getTools({
          ids: toolIds.join(","),
          limit: toolIds.length,
          includeDeleted: true,
        });
        setTools(toolsResponse?.rows);
      }
      setProjectTools(toolInventory?.rows);
      setToolCount(toolInventory?.count);
    } catch (error) {
      console.log("🚀 ~ fetchTools ~ error:", error);
    } finally {
      setToolsLoading(false);
    }
  };

  const fetchDocuments = async (projectId) => {
    try {
      setDocsLoading(true);
      const { data } = await ProjectService.getDocuments(projectId, { limit: 20 });
      setDocuments(Array.isArray(data?.rows) ? data.rows : []);
      setDocCount(data?.count || 0);
    } catch (error) {
      console.error("🚀 ~ fetchDocuments ~ error:", error);
    } finally {
      setDocsLoading(false);
    }
  };

  const handleTabChange = (tab) => {
    setActiveTab(tab);
    navigateTab(`/projects/${id}?tab=${tab}`);
  };

  const openDeleteModal = () => {
    setDeleteModal({
      isOpen: true,
      projectId: id,
    });
  };

  const closeDeleteModal = () => {
    setDeleteModal({
      isOpen: false,
      projectId: null,
    });
  };

  const deleteProject = async () => {
    try {
      await ProjectService.deleteProject(deleteModal.projectId);
      toast.success("Project deleted successfully");
      closeDeleteModal();
      navigate(PROJECT_ROUTES.root, { replace: true });
    } catch (error) {
      toast.error("Failed to delete project");
      console.error("🚀 ~ deleteProject ~ error:", error);
    }
  };

  const handleReloadDetails = () => setReloadDetails((prev) => prev + 1);

  const handleReloadPg = () => fetchPgData(id);

  const handleReloadTools = () => {
    if (activeTab === "tools") setReloadTools((prev) => prev + 1);
    else fetchTools(projectDetails?.storeId);
  };

  const handleDownloadReport = () => {
    navigate(`/reports/project?projectId=${id}`);
  };

  const handleViewDoc = async (doc) => {
    try {
      setViewingDocId(doc.id);
      const res = await FileService.getFileDetails(doc.id);
      const fileUrl = res.url;

      if (fileUrl) {
        window.open(fileUrl, "_blank");
      } else {
        toast.error("Failed to fetch secure document link");
      }
    } catch (error) {
      console.error("View error:", error);
      toast.error("Error retrieving document securely");
    } finally {
      setViewingDocId(null);
    }
  };

  const handleDeleteDoc = async (fileId) => {
    try {
      await ProjectService.deleteDocument(id, fileId);
      toast.success("Document deleted successfully");
      fetchDocuments(id);
      closeDocDeleteModal();
    } catch (error) {
      console.error("Delete document error:", error);
      toast.error("Failed to delete document");
    }
  };

  const openDocDeleteModal = (docId) => {
    setDocDeleteModal({
      isOpen: true,
      docId,
    });
  };

  const closeDocDeleteModal = () => {
    setDocDeleteModal({
      isOpen: false,
      docId: null,
    });
  };

  const handleAddInvoice = () => {
    if (!projectDetails?.clientId) {
      toast.error("Client is required to create invoice for this project");
      return;
    }

    navigate(`/client/invoices/add?projectId=${id}&clientId=${projectDetails.clientId}`);
  };

  const overviewActions = [
    projectDetails?.isGovernment &&
      hasCreateInvoice && {
        icon: <DescriptionOutlined />,
        tooltip: "Create GST Invoice",
        onClick: handleAddInvoice,
      },
    hasDownloadReport && {
      icon: <FileDownloadOutlined />,
      tooltip: "Download Report",
      onClick: handleDownloadReport,
    },
    hasDeleteProject && {
      icon: <Delete />,
      tooltip: "Delete",
      hoverStyles: { color: "#FF1744" },
      onClick: () => openDeleteModal(),
    },
  ].filter(Boolean);

  const incomeActions = [
    hasAddIncome && {
      icon: <Add />,
      buttonText: "Add Income",
      tooltip: "Add Income",
      onClick: () => navigate(`/add-income?projectId=${id}&tab=income`),
    },
  ];

  const expenseActions = [
    hasAddExpense && {
      icon: <Add />,
      buttonText: "Add Expense",
      toolTip: "Add Expense",
      onClick: () => navigate(`/expense/add?projectId=${id}&tab=expense`),
    },
  ];

  const workActions = [
    hasAddWork && {
      icon: <WorkOutline />,
      buttonText: "Add Work",
      toolTip: "Add Work",
      onClick: () => navigate(`/add-work?projectId=${id}&tab=works`),
    },
  ];

  const purchaseActions = [
    hasAddPurchase && {
      icon: <WorkOutline />,
      buttonText: "Add Purchase",
      toolTip: "Add Purchase",
      onClick: () => navigate(`/vendor/purchase/add?projectId=${id}&tab=purchases`),
    },
  ];

  const contractActions = [
    hasCreateTransaction && {
      icon: <Payment />,
      buttonText: "Add Payment",
      tooltip: "Add Payment",
      onClick: () => navigate(`/transaction/add?projectId=${id}&tab=contracts`),
    },
  ];

  const pgActions = [
    hasCreatePerformanceGuarantee && {
      icon: <Add />,
      buttonText: "Add PG & AD PG",
      tooltip: "Add PG & AD PG",
      onClick: () => setOpenPgModal(true),
    },
  ];

  const toolActions = [
    hasToolCreate && {
      icon: <Add />,
      buttonText: "Add Tool",
      tooltip: "Add Tool",
      onClick: () => setOpenToolModal(true),
    },
  ];

  const renderActiveTab = () => {
    switch (activeTab) {
      case "overview":
        return (
          <>
            <Box sx={{ position: "relative", zIndex: 1 }}>
              <ProjectHeader
                title={projectDetails?.name}
                chipText={projectDetails?.isGovernment && "Government Project"}
                actionButtons={overviewActions}
                cards={[
                  {
                    title: "Total Income",
                    value: formatRupees(projectSummary?.totalIncome),
                    color: "#00C853",
                    icon: <TrendingUp />,
                  },
                  {
                    title: "Total Expense",
                    value: formatRupees(projectSummary?.totalExpense),
                    color: "#FF1744",
                    icon: <TrendingDown />,
                  },
                  { title: "Profit", value: formatRupees(projectSummary?.profit), color: "#674BF5", icon: <TrendingUp /> },
                ]}
              />
            </Box>

            <Box
              sx={{
                display: "grid",
                gridTemplateColumns: {
                  xs: "1fr",
                  md: "1fr 1fr",
                },
                width: "100%",
                padding: "11px",
                boxSizing: "border-box",
              }}
            >
              {/* left column */}
              <Box sx={{ display: "flex", flexDirection: "column" }}>
                {/* project details */}
                <ProjectDetails projectDetails={projectDetails} reloadData={handleReloadDetails} />
                {/* tender details */}
                {projectDetails?.isGovernment && <TenderDetails projectDetails={projectDetails} />}
                {/* task overview */}
                {(hasViewAllTask || hasViewAssignedTask) && <TaskOverview />}
                {/* project actions */}
                {(hasDownloadReport || hasDeleteProject) && (
                  <ProjectActions onDownload={handleDownloadReport} onDelete={openDeleteModal} />
                )}
              </Box>

              {/* right column */}
              <Box sx={{ display: "flex", flexDirection: "column" }}>
                {/* budget overview */}
                <BudgetOverview />

                {(hasClientInfo || hasTeamInfo) && (
                  <Box
                    sx={{
                      display: "flex",
                      flexWrap: "wrap",
                    }}
                  >
                    {hasTeamInfo && (
                      <Box sx={{ flex: "1 1 50%" }}>
                        {/* team info */}
                        <TeamInfo />
                      </Box>
                    )}
                    {hasClientInfo && (
                      <Box sx={{ flex: hasTeamInfo ? "1 1 50%" : "1 1 100%" }}>
                        {/* customer info */}
                        <CustomerInfo projectDetails={projectDetails} />
                      </Box>
                    )}
                  </Box>
                )}

                {/* stage overview */}
                {hasViewStage && <StageOverview />}
                {/* PG and AD PG */}
                {projectDetails?.isGovernment && hasViewPg && (
                  <CollapsibleCard
                    loading={pgLoading}
                    title={`PG and AD PG (${performanceGuarantees?.count})`}
                    icon={<SavingsOutlined />}
                    headerButtons={[
                      ...pgActions,
                      hasViewPg && {
                        icon: <VisibilityOutlined />,
                        buttonText: "View All",
                        tooltip: "View All PG and AD PG",
                        onClick: () => {
                          navigate(`performance-guarantees`);
                        },
                      },
                    ]}
                    items={performanceGuarantees?.rows?.map((pgData) => {
                      return {
                        name: pgData?.name,
                        value: pgData.depositAmount ? `₹ ${pgData.depositAmount}` : "₹ 0",
                      };
                    })}
                    emptyState={{ title: "No PG and AD PG found", subtitle: "Add PG and AD PG to get started" }}
                  />
                )}
                {/* invoices */}
                {projectDetails?.isGovernment && hasViewInvoice && (
                  <CollapsibleCard
                    loading={invoicesLoading}
                    title={`GST Invoices`}
                    icon={<DescriptionOutlined />}
                    headerButtons={[
                      {
                        icon: <VisibilityOutlined />,
                        buttonText: "View All",
                        tooltip: "View All GST invoices",
                        onClick: () => {
                          navigate(`invoices`);
                        },
                      },
                    ]}
                    items={invoices?.map((invoice) => {
                      return {
                        name: invoice?.invoiceNumber,
                        value: invoice?.totalAmount ? `₹ ${invoice.totalAmount}` : "₹ 0",
                      };
                    })}
                    emptyState={{ title: "No GST invoices found", subtitle: "Add GST invoices to get started" }}
                  />
                )}

                {/* documents info */}
                <CollapsibleCard
                  loading={docsLoading}
                  title={`Documents (${docCount})`}
                  icon={<DescriptionOutlined />}
                  headerButtons={[
                    hasEditProject && {
                      icon: <Add />,
                      buttonText: "Add Document",
                      tooltip: "Add Document",
                      onClick: () => setOpenDocModal(true),
                    },
                  ].filter(Boolean)}
                  itemButtons={[
                    {
                      icon: <VisibilityOutlined />,
                      tooltip: "View",
                      onClick: (item) => handleViewDoc(item),
                    },
                    hasEditProject && {
                      icon: <Delete />,
                      tooltip: "Delete",
                      hoverStyles: { color: "#FF1744" },
                      onClick: (item) => openDocDeleteModal(item.id),
                    },
                  ].filter(Boolean)}
                  items={
                    Array.isArray(documents)
                      ? documents.map((doc) => {
                          return {
                            ...doc,
                            date: get_date(doc.createdAt),
                            name: doc.originalName,
                          };
                        })
                      : []
                  }
                  emptyState={{ title: "No Documents found", subtitle: "Add Documents to get started" }}
                />
                {/* tools info */}
                {hasViewTool && (
                  <CollapsibleCard
                    loading={toolsLoading}
                    icon={<BuildOutlined />}
                    title={`Tools (${toolCount})`}
                    headerButtons={[
                      ...toolActions,
                      hasViewTool && {
                        icon: <VisibilityOutlined />,
                        buttonText: "All tools",
                        tooltip: "View",
                        onClick: () => setActiveTab("tools"),
                      },
                    ]}
                    items={projectTools.map((toolInv) => {
                      const assignedTool = tools.find((t) => t.id === toolInv.toolId);
                      return {
                        date: get_date(toolInv.updatedAt || toolInv.createdAt),
                        name: assignedTool?.name || "Unknown Tool",
                        value: toolInv.quantity ?? 0,
                      };
                    })}
                    emptyState={{ title: "No Tools found", subtitle: "Add Tools to get started" }}
                  />
                )}
              </Box>
            </Box>
          </>
        );
      case "income":
        return (
          <>
            <ProjectHeader title="Income Details" actionButtons={incomeActions} />
            <ProjectIncome />
          </>
        );
      case "expense":
        return (
          <>
            <ProjectHeader title="Expense Details" actionButtons={expenseActions} />
            <ExpenseList projectId={id} />
          </>
        );
      case "works":
        return (
          <>
            <ProjectHeader title="Works Details" actionButtons={workActions} />
            <ProjectWork />
          </>
        );
      case "purchases":
        return (
          <>
            <ProjectHeader title="Purchase Details" actionButtons={purchaseActions} />
            <ProjectPurchase />
          </>
        );
      case "contracts":
        return (
          <>
            <ProjectHeader title="Contract Payments" actionButtons={contractActions} />
            <ProjectContracts />
          </>
        );
      case "tools":
        return (
          <>
            <ProjectHeader title="Tools Details" actionButtons={toolActions} />
            <ProjectTools storeId={projectDetails?.storeId} reloadData={reloadTools} />
          </>
        );
      default:
        return null;
    }
  };

  const allTabs = [
    { key: "overview", permission: hasViewOverview },
    { key: "income", permission: hasAddIncome },
    { key: "expense", permission: hasViewExpense },
    { key: "works", permission: hasAddWork },
    { key: "purchases", permission: hasAddPurchase },
    { key: "contracts", permission: hasCreateTransaction },
    { key: "tools", permission: hasViewTool },
  ];

  const allowedTabs = allTabs.filter((tab) => tab.permission === true).map((tab) => tab.key);

  return (
    <>
      <PageLayout>
        <PageHeaderTabs title="Project Overview" tabs={allowedTabs} activeTab={activeTab} onTabChange={handleTabChange} />

        {loading ? (
          <Box sx={{ display: "flex", justifyContent: "center", alignItems: "center", height: "50vh" }}>
            <CircularProgress sx={{ color: "#674BF5" }} />
          </Box>
        ) : (
          renderActiveTab()
        )}
      </PageLayout>

      {/* Delete Project */}
      <DeleteModal
        isOpen={deleteModal.isOpen}
        onClose={closeDeleteModal}
        onConfirm={deleteProject}
        message="Want to delete this project?"
      />

      {/* Delete Document */}
      <DeleteModal
        isOpen={docDeleteModal.isOpen}
        onClose={closeDocDeleteModal}
        onConfirm={() => handleDeleteDoc(docDeleteModal.docId)}
        message="Are you sure you want to delete this document?"
      />

      {/* add stage modal */}
      <AddEditProjectStage open={openStageModal} onClose={() => setOpenStageModal(false)} />

      {/* add pg and ad pg modal */}
      <AddPerformanceGuarantees
        open={openPgModal}
        projectId={id}
        onClose={() => setOpenPgModal(false)}
        onSuccess={handleReloadPg}
      />

      {/* move tool modal */}
      <MoveTool
        open={openToolModal}
        projectStoreId={projectDetails?.storeId}
        onClose={() => setOpenToolModal(false)}
        onSuccess={handleReloadTools}
      />

      {/* add document modal */}
      <AddDocumentModal
        open={openDocModal}
        projectId={id}
        onClose={() => setOpenDocModal(false)}
        onSuccess={() => fetchDocuments(id)}
        currentCount={docCount}
      />
    </>
  );
};
